import * as React from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { MenuItem, ListItemText, ListItemIcon, Button, TextField, Grid } from '@mui/material';
import { Edit, Delete, StarBorder } from '@mui/icons-material';
import SourceEditPopup from './SourceEditPopup';
import { REMOTE_API, getDataUpstream } from '../utils';
import { Confirm, useNotify } from 'react-admin';
import { IChoice, ISource, ISourceVariable } from '../types';

const SOURCE_LINK = 'https://api.songtek.net/csv/merge/filter/';

/** Props for {@link SourceSelect}. */
export interface ISourceSelectProps {
    /** Field label. */
    label: string;
    /** Known sources to list. */
    choices: ISource[];
    /** Known market types, forwarded to `SourceEditPopup`. */
    marketType: IChoice[];
    /** Known `SourceVariable` records, forwarded to `SourceEditPopup`. */
    sourceVariable: ISourceVariable[];
    /** Data provider used to fetch/delete sources. */
    dataProvider: any;
    /** Called with the selected source name. */
    onSelect: (value: string) => void;
    /** Called after a source is deleted, so the caller can reload the source list. */
    onDeleted: () => void;
}

/** {@link SourceSelect}'s local dialog/link state. */
interface ISourceSelectState {
    /** Displayed source filter link. */
    link: string;
    /** Whether the link `TextField` is hidden. */
    hidden: boolean;
    /** Whether the edit dialog is open. */
    isEdit: boolean;
    /** Whether the delete confirmation dialog is open. */
    isConfirm: boolean;
    /** Name of the source pending deletion. */
    sourceNameDel: string;
    /** Source currently being edited, or `null`. */
    source: ISource | null;
}

/**
 * Source picker for the CSV Merge page: a `Select` of known sources (with
 * per-row edit/delete actions), an optional link display, and the
 * edit/delete dialogs it drives.
 * @param props - `label`, `choices`, `marketType`, `sourceVariable`, `dataProvider`, `onSelect`, `onDeleted`.
 */
const SourceSelect: React.FC<ISourceSelectProps> = ({ label, choices, marketType, sourceVariable, dataProvider, onSelect, onDeleted }) => {
    const notify = useNotify();
    const [state, setState] = React.useState<ISourceSelectState>({
        link: `${SOURCE_LINK}_`,
        hidden: true,
        isEdit: false,
        isConfirm: false,
        sourceNameDel: '', // for delete
        source: null
    });

    const handleChange = (e: SelectChangeEvent) => {
        const { value } = e.target;
        setState((prev) => ({ ...prev, link: `${SOURCE_LINK}${value}` }));
        onSelect(value);
    };

    const onEdit = (item: ISource) => {
        // Fetch data source
        dataProvider.fetchData(REMOTE_API, `sources/${item.Name}`)
            .then(function (response: any) {
                setState((prev) => ({ ...prev, isEdit: true, source: { ...response.data, ...getDataUpstream(response.data.Url) } }));
            }).catch((error: any) => {
                setState((prev) => ({ ...prev, isEdit: false }));
                notify(`Get source was error: ${error}`, { type: 'error' });
            });
    };

    const onDelete = (item: ISource) => {
        setState((prev) => ({ ...prev, isConfirm: true, sourceNameDel: item.Name }));
    };

    const handleDelete = () => {
        setState((prev) => ({ ...prev, isConfirm: false }));
        dataProvider.delete(REMOTE_API, `sources/${state.sourceNameDel}`)
            .then(function () {
                notify('source deleted', { type: 'success' });
                onDeleted(); // To reload source
            }).catch((error: any) => {
                console.error(error);
            });
    };

    return (
        <Grid container spacing={1}>
            <Grid size={3}>
                <FormControl sx={{ minWidth: '100%' }} size="small">
                    <InputLabel id="demo-select-small">{label}</InputLabel>
                    <Select
                        labelId="demo-select-small"
                        id="demo-select-small"
                        defaultValue={'View All'}
                        label={label}
                        onChange={handleChange}
                        renderValue={(selected) => {
                            const selectedChoice = choices.find((choice) => choice.Name === selected);
                            return selectedChoice ? selectedChoice.Name : 'View All';
                        }}
                    >
                        <MenuItem key={-1} id={'_'} value={'_'}>
                            <ListItemIcon>
                                <StarBorder />
                            </ListItemIcon>
                            <ListItemText primary={'View All'} />
                        </MenuItem>
                        {choices.map((m, index: number) => {
                            return (
                                <MenuItem key={index} id={m.Id as any} value={m.Name}>
                                    <ListItemText primary={m.Name} />
                                    <ListItemIcon>
                                        <Button startIcon={<Edit />} sx={{ marginRight: 1 }} onClick={() => onEdit(m)}>Edit</Button>
                                        <Button startIcon={<Delete />} onClick={() => onDelete(m)}>Delete</Button>
                                    </ListItemIcon>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={3} sx={{ display: state.hidden ? 'none' : '' }}>
                <TextField
                    autoComplete="link"
                    value={state.link}
                    sx={{ width: '100%' }}
                    onChange={(e) => setState((prev) => ({ ...prev, link: `${SOURCE_LINK}${e.target.value}` }))}
                />
            </Grid>
            <Grid size={3}>
                <Button size='small'
                    sx={{ marginTop: 4 }}
                    onClick={() => setState((prev) => ({ ...prev, hidden: false }))}>
                    Show Link
                </Button>
            </Grid>
            {state.isEdit && <SourceEditPopup data={state as any} sourceVariable={sourceVariable} marketType={marketType} dataProvider={dataProvider} handleClose={() => setState((prev) => ({ ...prev, isEdit: false }))} />}
            <Confirm isOpen={state.isConfirm} title={'Delete Source'} content={`You are about to delete source "${state.sourceNameDel}". Are you sure?`} onConfirm={handleDelete} onClose={() => setState((prev) => ({ ...prev, isConfirm: false }))} />
        </Grid>
    );
};

export default SourceSelect;
